import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import {
  applyClassToCharacters,
  customOption,
  LoadingSpinner,
} from "../../../constants/const";
import { addMember } from "../../member/ApiMember";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";

const getDateValue = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const parts = raw.split(/[-/]/).map(Number);

  if (parts.length === 3 && parts.every((part) => !Number.isNaN(part))) {
    if (String(parts[0]).length === 4) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  const parsedDate = new Date(raw);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const getAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(Math.abs(Number(String(value).replace(/,/g, ""))));
};

const findOption = (options, values) =>
  options.find((option) => {
    const optionValues = [
      option?.value,
      option?.id,
      option?.label,
      option?.label1,
      option?.name,
      option?.title,
    ].map((item) => String(item || "").trim().toLowerCase());

    return values.some((value) =>
      optionValues.includes(String(value || "").trim().toLowerCase()),
    );
  }) || null;

const CustomInput = ({ label, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input type="text" value={value} onChange={onChange} className="no-drag" />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const EditOtherIncomeLoanFromMarkaz = ({
  setModal,
  transactionNo,
  transaction,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [unitBankAccounts, setUnitBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [formData, setFormData] = useState({
    selectedFinanceCategory: transaction?.category || null,
    selectedAccount: null,
    date: getDateValue(transaction?.date),
    amount: getAmount(transaction?.amount),
    memo: transaction?.memo || "",
    reference: transaction?.reference || "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    bankAccounts: false,
    accounts: false,
    submit: false,
  });

  const isBusy = Object.values(loading).some(Boolean);
  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    if (!unitId) return;

    setLoading((prev) => ({ ...prev, bankAccounts: true }));
    addMember()
      .GetUnitBankAccounts(unitId)
      .then((res) => {
        const list = res.data || [];
        setUnitBankAccounts(list);
        setSelectedBankAccount(
          findOption(list, [
            transaction?.rafiqBankId,
            transaction?.bankAccountId,
            transaction?.bankName,
          ]) ||
            list[0] ||
            null,
        );
      })
      .catch((err) => console.error(err))
      .finally(() =>
        setLoading((prev) => ({ ...prev, bankAccounts: false })),
      );
  }, [transaction, unitId]);

  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true }));
    ApiFinance()
      .GetFinanceCategoryAccountsById(categoryId)
      .then((res) => {
        const accounts = res.data || [];
        setFormData((prev) => ({
          ...prev,
          selectedAccount:
            findOption(accounts, [transaction?.codeId, transaction?.accountName]) ||
            accounts[0] ||
            null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load income heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, accounts: false })));
  }, [formData.selectedFinanceCategory, transaction]);

  const validate = () => {
    const nextErrors = {
      amount: formData.amount ? (
        ""
      ) : (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      ),
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const payload = {
      transactionNo,
      financeAccount: formData.selectedAccount ?? null,
      rafiqBankId: selectedBankAccount?.value ?? null,
      category: formData.selectedFinanceCategory ?? null,
      date: formData.date,
      amount: formData.amount,
      memo: formData.memo,
      reference: formData.reference || "",
      unitId: unitId || transaction?.unitId,
    };

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .RequestModificationOtherIncomeLoanFromMarkaz(payload)
      .then((res) => {
        const data = res.data || {};
        if (Number(data.statusCode) !== 200) {
          swal({
            title: "Error",
            text: data.message || "Failed to update Other Income",
            icon: "error",
            buttons: "OK",
          });
          return;
        }

        swal({
          title: "Success",
          text: t(data.message) || t("member_member_detail_fund_edited"),
          icon: "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") onSuccess();
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update Other Income.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => setLoading((prev) => ({ ...prev, submit: false })));
  };

  return (
    <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
      <Modal isOpen className="inmodal" size="lg" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="funds_other_income_loan_from_markaz" />
        </ModalHeader>

        {isBusy ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="fund_bank_account" />
                    </Label>
                    <Select
                      components={{ Option: customOption }}
                      options={unitBankAccounts}
                      value={
                        selectedBankAccount
                          ? {
                              value: selectedBankAccount.value,
                              label: applyClassToCharacters(
                                selectedBankAccount.label,
                                "ur",
                              ),
                            }
                          : null
                      }
                      onChange={setSelectedBankAccount}
                      placeholder={t("member_select")}
                      classNamePrefix="select"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>
                </Col>
                <Col md="3">
                  <CustomInput
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>
                <Col md="6">
                  <CustomInput
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                color="white"
                className={GetCustomStyles().btn_style}
                onClick={toggle}
              >
                <GetLanguageString props="common_cancel" />
              </Button>
              <Button
                color="primary"
                className={GetCustomStyles().btn_style}
                disabled={isBusy}
                type="submit"
              >
                <GetLanguageString props="common_save_changes" />
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditOtherIncomeLoanFromMarkaz;
